import { STORAGE_KEYS, storage } from '@/lib/storage/keys';
import { PAGE_PATHS } from './routes';

/**
 * ONE FRONT DOOR (docs/behavior/measure-legal-auth.md §3.3, §6.6).
 *
 * 2026-09-17, on ☧ 0's instruction: /sovereign-stack now carries the consent tick and the
 * passwordless magic-link intake itself (CaptureForm.tsx), which is what ΩΞ-MARKET-004 Ruling 2
 * required of the door. It therefore sends no one elsewhere (the old /go/sovereign-stack page was
 * removed on 2026-09-26 and 301s to /sovereign-stack at the edge), and nothing
 * here rewrites a link. `hasLiveSession` is unchanged and still reports the session token.
 * Reversible: restore the pattern below and the two lines in public/.htaccess.
 */

/** Where a signed-in click on a link that was rewritten while signed out is sent. */
export const VAULT_ARRIVAL = `${PAGE_PATHS['sovereign-stack']}?vault=1`;

export function hasLiveSession(now: number = Date.now()): boolean {
  const token = storage.get(STORAGE_KEYS.accessToken);
  if (token === null || token === '') return false;
  const expires = Number(storage.get(STORAGE_KEYS.tokenExpires));
  return !expires || now < expires;
}

/** Paths still gated behind the front door. Empty since 2026-09-17; see the note above. */
const GATED_PATHS: readonly string[] = [];

export function isVaultPath(pathname: string): boolean {
  return GATED_PATHS.includes(pathname.replace(/\/$/, ''));
}

/** The intake URL carrying the original query and fragment. */
export function intakeUrl(search: string, hash: string): string {
  return PAGE_PATHS['sovereign-stack'] + search + hash;
}

export interface FrontDoorHref {
  readonly href: string;
  /** True when the href was redirected to the intake (rendered as `data-m618-rewritten=""`). */
  readonly rewritten: boolean;
}

/**
 * The href a link to the vault route must carry. Signed-out visitors get the intake URL;
 * every other href is returned unchanged.
 */
export function frontDoorHref(href: string, signedIn: boolean, base: string): FrontDoorHref {
  if (signedIn || !href.includes('sovereign-stack')) return { href, rewritten: false };
  let url: URL;
  try {
    url = new URL(href, base);
  } catch {
    return { href, rewritten: false };
  }
  if (url.origin !== new URL(base).origin || !isVaultPath(url.pathname)) return { href, rewritten: false };
  return { href: intakeUrl(url.search, url.hash), rewritten: true };
}
